import React, { useState, useEffect, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { useQueryClient } from '@tanstack/react-query'
import { RootState, AppDispatch } from '../store'
import { clearUser } from '../store/authSlice'
import { fetchInvitations, addInvitation } from '../store/invitationsSlice'
import authService from '../services/authService'
import { webSocketService } from '../services/websocket'
import Logo from './Logo'
import LanguageSelector from './LanguageSelector'
import ThemeToggle from './ThemeToggle'
import { useTranslation } from '../hooks/use-i18n'
import { isInvitationRecipient } from '../types/invitation'

const Navigation: React.FC = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const queryClient = useQueryClient()
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth)
  const { unreadCount, invitations } = useSelector((state: RootState) => state.invitations)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isAuthenticated) dispatch(fetchInvitations())
  }, [dispatch, isAuthenticated])

  useEffect(() => {
    const userId = user?._id
    if (!isAuthenticated || !userId) return
    webSocketService.connectForNotifications(userId)
    const onInvitationNew = (payload: any) => {
      if (payload && payload._id) dispatch(addInvitation(payload))
    }
    const onInvitationUpdated = () => dispatch(fetchInvitations())
    const onRoomsRefresh = () => {
      queryClient.invalidateQueries({ queryKey: ['user-academ-rooms'] })
      queryClient.invalidateQueries({ queryKey: ['user-rooms'] })
    }
    webSocketService.on('invitation:new', onInvitationNew)
    webSocketService.on('invitation:updated', onInvitationUpdated)
    webSocketService.on('rooms:refresh', onRoomsRefresh)
    return () => {
      webSocketService.off('invitation:new', onInvitationNew)
      webSocketService.off('invitation:updated', onInvitationUpdated)
      webSocketService.off('rooms:refresh', onRoomsRefresh)
    }
  }, [isAuthenticated, user?._id, dispatch, queryClient])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false)
      }
    }
    if (isMobileMenuOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isMobileMenuOpen])

  const handleLogout = () => {
    authService.logout()
    dispatch(clearUser())
    webSocketService.disconnect()
    navigate('/')
    setIsMobileMenuOpen(false)
  }

  // Guest marketing pages use LandingNavbar (mint chrome + Demo/About).
  if (
    (location.pathname === '/' && !isAuthenticated) ||
    location.pathname === '/invite' ||
    location.pathname === '/demo' ||
    location.pathname === '/about-us'
  )
    return null

  const linkClass = (active: boolean) =>
    `text-sm font-semibold transition-colors ${
      active ? 'text-[#00413E]' : 'text-gray-500 hover:text-[#0B3333]'
    }`

  const homeActive =
    location.pathname === '/' ||
    location.pathname === '/dashboard' ||
    location.pathname.startsWith('/dashboard/') ||
    location.pathname.startsWith('/solo/') ||
    location.pathname.startsWith('/academ/') ||
    location.pathname.startsWith('/room/') ||
    location.pathname.startsWith('/formula1/') ||
    location.pathname.startsWith('/chess/')
  const learnActive = location.pathname === '/learn' || location.pathname.startsWith('/learn/')
  const improveActive =
    location.pathname === '/improve' || location.pathname.startsWith('/improve/')
  const progressActive = location.pathname === '/progress' || location.pathname === '/weekly'
  const accountActive =
    location.pathname === '/account' ||
    location.pathname === '/invitations' ||
    location.pathname === '/payments'
  const pendingCount = user
    ? invitations.filter(
        (inv) => isInvitationRecipient(inv, user) && inv.status === 'pending',
      ).length
    : unreadCount

  const authLinks = (
    <>
      <Link to="/dashboard/solos" className={linkClass(homeActive)} onClick={() => setIsMobileMenuOpen(false)}>
        {t('nav.home')}
      </Link>
      <Link to="/learn/subject" className={linkClass(learnActive)} onClick={() => setIsMobileMenuOpen(false)}>
        {t('nav.learn')}
      </Link>
      <Link to="/improve/mistakes" className={linkClass(improveActive)} onClick={() => setIsMobileMenuOpen(false)}>
        {t('nav.improve')}
      </Link>
      <Link to="/progress" className={linkClass(progressActive)} onClick={() => setIsMobileMenuOpen(false)}>
        {t('nav.progress')}
      </Link>
      <Link
        to="/account"
        className={`relative ${
          pendingCount > 0 && !accountActive
            ? 'text-sm font-semibold text-red-600 hover:text-red-700'
            : linkClass(accountActive)
        }`}
        onClick={() => setIsMobileMenuOpen(false)}
      >
        {t('nav.account')}
        {pendingCount > 0 && (
          <span className="absolute -top-1.5 -right-2 bg-red-500 text-white text-[9px] font-bold rounded-full h-4 w-4 flex items-center justify-center">
            {pendingCount > 9 ? '9+' : pendingCount}
          </span>
        )}
      </Link>
    </>
  )

  return (
    <nav
      ref={menuRef}
      className="fixed top-0 left-0 right-0 z-50 border-b border-[#0B3333]/10 bg-white/95 backdrop-blur w-full pt-[env(safe-area-inset-top,0px)]"
    >
      <div className="mx-auto flex items-center justify-between min-h-[3.5rem] sm:min-h-[4.25rem] py-2 px-3 sm:px-4 w-full max-w-[900px]">
        <Logo
          size="sm"
          showCredit
          to={isAuthenticated ? '/dashboard/solos' : '/'}
        />

        <div className="hidden md:flex items-center gap-6">
          {isAuthenticated ? (
            <>
              {authLinks}
              <ThemeToggle />
              <LanguageSelector />
              <button
                onClick={handleLogout}
                className="text-sm text-gray-500 hover:text-[#0B3333] font-medium"
              >
                {t('nav.signOut')}
              </button>
            </>
          ) : (
            <>
              <ThemeToggle />
              <LanguageSelector />
              <Link
                to="/login"
                className="px-4 py-2 rounded-full bg-[#00413E] text-white text-sm font-semibold"
              >
                {t('nav.signIn')}
              </Link>
            </>
          )}
        </div>

        <div className="md:hidden flex items-center gap-1">
          <ThemeToggle iconSize={20} />
          <LanguageSelector />
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="text-[#0B3333] p-2.5 rounded-xl hover:bg-[#E6F4EC] active:scale-[0.96] transition-all"
            aria-label={t('nav.toggleMenu')}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.25} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.25} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden border-t border-[#0B3333]/10 bg-white py-4 px-6 flex flex-col gap-4">
          {isAuthenticated ? (
            <>
              {authLinks}
              <button onClick={handleLogout} className="text-left text-sm text-gray-500">
                {t('nav.signOut')}
              </button>
            </>
          ) : (
            <Link
              to="/login"
              onClick={() => setIsMobileMenuOpen(false)}
              className="px-5 py-2.5 rounded-full bg-[#00413E] text-white text-sm font-semibold text-center"
            >
              {t('nav.signIn')}
            </Link>
          )}
        </div>
      )}
    </nav>
  )
}

export default Navigation
